import React, { useEffect, useState } from "react";
import { Loader2, Award, Download, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { toast } from "sonner";

const BLANK = {
  reference: "", applicant: "", property_label: "", financing_type: "", amount: "",
  valid_until: "", salutation: "", intro: "", conditions: "", closing: "",
  signatory_name: "Raahul Reddy", signatory_title: "Head of Finance",
  email: "", phone: "",
  purchase_price: "", arv: "",
};

function Field({ label, k, f, set, textarea, rows, type, testid }) {
  return (
    <div>
      <label className="text-xs text-dim mb-1 block">{label}</label>
      {textarea ? (
        <Textarea value={f[k] || ""} onChange={set(k)} rows={rows || 3} className="bg-surface/70 border-edge text-white" data-testid={testid} />
      ) : (
        <Input value={f[k] || ""} onChange={set(k)} type={type || "text"} className="bg-surface/70 border-edge text-white h-11" data-testid={testid} />
      )}
    </div>
  );
}

export default function AdminPreapprovals() {
  const [f, setF] = useState(null);
  const [defaults, setDefaults] = useState({});
  const [subs, setSubs] = useState([]);
  const [selected, setSelected] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/preapproval-defaults")
      .then((r) => { setDefaults(r.data); setF({ ...BLANK, ...r.data }); })
      .catch(() => setF({ ...BLANK }));
    api.get("/admin/submissions").then((r) => setSubs(r.data || [])).catch(() => {});
  }, []);

  const set = (k) => (e) => setF((prev) => ({ ...prev, [k]: e.target.value }));

  const loadDeal = async (val) => {
    setSelected(val);
    if (!val) { setF({ ...BLANK, ...defaults }); return; }
    const [type, id] = val.split("::");
    try {
      const { data } = await api.get(`/admin/submissions/${type}/${id}/preapproval-draft`);
      const sub = subs.find((s) => s.id === id) || {};
      setF((prev) => ({ ...prev, ...data, reference: sub.reference || "" }));
      toast.success("Loaded from deal");
    } catch (e) { toast.error("Could not load that deal"); }
  };

  const download = async () => {
    setBusy(true);
    try {
      const res = await api.post("/admin/preapproval-letter", f, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${f.reference || "BullsEye"}-PreApproval.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Pre-approval letter generated");
    } catch (e) { toast.error("Could not generate letter"); }
    finally { setBusy(false); }
  };

  const reset = () => { setSelected(""); setF({ ...BLANK, ...defaults }); };

  if (!f) return <div className="py-20 grid place-items-center"><Loader2 className="w-8 h-8 text-gold animate-spin" /></div>;

  return (
    <div data-testid="admin-preapprovals">
      <h1 className="h-display text-3xl text-white">Pre-Approval Letters</h1>
      <p className="mt-1 text-dim">Generate a branded, fully editable BullsEye Capital pre-approval letter — from scratch or from an existing deal.</p>

      <div className="mt-6 card-surface rounded-xl p-5 space-y-4">
        <div>
          <label className="text-xs text-dim mb-1 block">Prefill from an existing deal (optional)</label>
          <div className="flex flex-col sm:flex-row gap-2">
            <select value={selected} onChange={(e) => loadDeal(e.target.value)}
              className="flex-1 h-11 rounded-md bg-surface/70 border border-edge text-white text-sm px-3" data-testid="preapproval-deal-select">
              <option value="">— Start from a blank letter —</option>
              {subs.map((s) => <option key={s.id} value={`${s.type}::${s.id}`}>{s.reference} · {s.applicant_name || s.email}</option>)}
            </select>
            <Button onClick={reset} variant="outline" className="btn-outline-glass rounded-lg h-11 gap-2" data-testid="preapproval-reset"><RotateCcw className="w-4 h-4" /> Reset</Button>
          </div>
        </div>

        <div className="border-t border-edge pt-4 grid sm:grid-cols-2 gap-3">
          <Field label="Reference (optional)" k="reference" f={f} set={set} testid="pa-reference" />
          <Field label="Applicant" k="applicant" f={f} set={set} testid="pa-applicant" />
          <Field label="Property" k="property_label" f={f} set={set} testid="pa-property" />
          <Field label="Financing Type" k="financing_type" f={f} set={set} testid="pa-financing-type" />
          <Field label="Pre-Approved Amount ($)" k="amount" f={f} set={set} type="number" testid="pa-amount" />
          <Field label="Valid Through" k="valid_until" f={f} set={set} testid="pa-valid-until" />
          <Field label="Purchase Price ($, optional)" k="purchase_price" f={f} set={set} type="number" testid="pa-purchase-price" />
          <Field label="After Repair Value ($, optional)" k="arv" f={f} set={set} type="number" testid="pa-arv" />
          <Field label="Greeting" k="salutation" f={f} set={set} testid="pa-salutation" />
        </div>

        <Field label="Opening Paragraph" k="intro" f={f} set={set} textarea rows={4} testid="pa-intro" />
        <Field label="Conditions Paragraph" k="conditions" f={f} set={set} textarea rows={5} testid="pa-conditions" />
        <Field label="Closing Line" k="closing" f={f} set={set} testid="pa-closing" />
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Signatory Name" k="signatory_name" f={f} set={set} testid="pa-signatory-name" />
          <Field label="Signatory Title" k="signatory_title" f={f} set={set} testid="pa-signatory-title" />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Contact Phone" k="phone" f={f} set={set} testid="pa-phone" />
          <Field label="Contact Email" k="email" f={f} set={set} testid="pa-email" />
        </div>

        <div className="pt-1">
          <Button onClick={download} disabled={busy} className="btn-gold rounded-lg h-11 px-6 gap-2" data-testid="generate-preapproval-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Award className="w-4 h-4" /> Generate &amp; Download Letter</>}
          </Button>
        </div>
      </div>
    </div>
  );
}
